import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';
import { leaveTypeOn } from '../utils/leaveLabels';
import { isOutNow, attendanceStatusLabel } from '../utils/attendanceStatus';
import { useMeetingsAccess } from '../utils/meetingsAccess';
import MeetingDrawer from '../components/meeting/MeetingDrawer';
import MeetingFormModal from '../components/meeting/MeetingFormModal';
import { RSVP_LABELS, dayText, rangeText } from '../components/meeting/meetingHelpers';
import '../styles/meetings.css';
import '../styles/Calendar.css'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faTh, faCalendarDay } from '@fortawesome/free-solid-svg-icons';

const CalendarPage = () => {
    const year = new Date().getFullYear();
    const [events, setEvents] = useState({});
    const [loading, setLoading] = useState(true);

    // --- VIEW STATE ---
    const [viewMode, setViewMode] = useState('slider'); // 'slider' | 'full'
    const [currentMonthIndex, setCurrentMonthIndex] = useState(new Date().getMonth());
    
    // Track direction for animation ('right' or 'left')
    const [slideDirection, setSlideDirection] = useState('right');

    // --- MEETINGS & EVENTS LAYER ---
    // A separate layer on top of the calendar above, for the pilot only. When
    // the server does not admit this person nothing below runs, nothing is
    // fetched, and the page renders exactly as it did before.
    const { enabled: meetingsEnabled } = useMeetingsAccess();
    const [meetings, setMeetings] = useState([]);
    const [selectedDay, setSelectedDay] = useState(null);
    const [openMeetingId, setOpenMeetingId] = useState(null);
    const [editingMeeting, setEditingMeeting] = useState(null);

    const loadMeetings = useCallback(async () => {
        try {
            // Dec 31, 23:59:59 rather than Jan 1: the server caps a window at 366 days.
            const res = await api.get('/meetings', {
                params: {
                    from: new Date(year, 0, 1).toISOString(),
                    to: new Date(year, 11, 31, 23, 59, 59, 999).toISOString()
                }
            });
            setMeetings(res.data || []);
        } catch (err) {
            // The calendar is still useful without them.
            console.error('Could not load meetings', err);
            setMeetings([]);
        }
    }, [year]);

    useEffect(() => {
        if (meetingsEnabled) loadMeetings();
    }, [meetingsEnabled, loadMeetings]);

    // Meetings by local calendar day. A multi-day entry sits on every day it spans.
    const meetingsByDay = useMemo(() => {
        const map = {};
        const key = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        meetings.forEach(m => {
            const start = new Date(m.startsAt);
            // An entry ending exactly at midnight does not belong to the next day.
            const end = new Date(new Date(m.endsAt).getTime() - 1);
            const last = end < start ? start : end;
            const day = new Date(start.getFullYear(), start.getMonth(), start.getDate());
            for (let i = 0; i < 62 && day <= last; i += 1) {
                (map[key(day)] = map[key(day)] || []).push(m);
                day.setDate(day.getDate() + 1);
            }
        });
        Object.values(map).forEach(list => list.sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt)));
        return map;
    }, [meetings]);

    const currentUser = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('user')) || {}; } catch { return {}; }
    }, []);

    const dayMeetings = selectedDay ? (meetingsByDay[selectedDay] || []) : [];
    const openMeeting = meetings.find(m => m._id === openMeetingId) || null;

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];

    useEffect(() => {
        fetchCalendarData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCalendarData = async () => {
        try {
            // 👇 NEW: Added WFH fetch to the Promise.all array
            const [holidaysRes, attendanceRes, leavesRes, wfhRes] = await Promise.all([
                api.get('/holidays'),
                api.get('/attendance/my-logs'),
                api.get('/leaves/my-leaves'),
                api.get('/wfh/my-requests').catch(() => ({ data: { history: [] } })) // Fallback if WFH API is missing
            ]);

            const leavesArray = leavesRes.data.history || []; 
            const wfhArray = wfhRes.data?.history || [];
            
            // Pass all data into the processor
            processEvents(holidaysRes.data, attendanceRes.data, leavesArray, wfhArray);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching calendar data", err);
            setLoading(false);
        }
    };

    const formatDateKey = (dateInput) => {
        const d = new Date(dateInput);
        if (isNaN(d.getTime())) return null;
        return d.toISOString().split('T')[0];
    };

    const processEvents = (holidaysData, attendanceLogs, leaveRequests, wfhRequests) => {
        const eventMap = {};
        
        // 1. Holidays
        if (Array.isArray(holidaysData)) {
            holidaysData.forEach(h => {
                const dateStr = formatDateKey(h.date);
                if (dateStr) eventMap[dateStr] = { type: 'holiday', label: h.name };
            });
        }

        // 2. Leaves
        if (Array.isArray(leaveRequests)) {
            leaveRequests.forEach(leave => {
                if (leave.status === 'Approved') {
                    const start = new Date(leave.fromDate);
                    const end = new Date(leave.toDate);
                    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                        const dateStr = formatDateKey(d);
                        if (dateStr && !eventMap[dateStr]) {
                            eventMap[dateStr] = { type: 'leave', label: leaveTypeOn(leave, d) };
                        }
                    }
                }
            });
        }

        // 3. Work From Home (WFH)
        if (Array.isArray(wfhRequests)) {
            wfhRequests.forEach(wfh => {
                if (wfh.status === 'Approved') {
                    const start = new Date(wfh.fromDate);
                    const end = new Date(wfh.toDate);
                    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                        const dateStr = formatDateKey(d);
                        if (dateStr && !eventMap[dateStr]) {
                            eventMap[dateStr] = { type: 'wfh', label: 'WFH' };
                        }
                    }
                }
            });
        }

        // 4. Attendance
        if (Array.isArray(attendanceLogs)) {
            attendanceLogs.forEach(log => {
                const [day, month, yearVal] = log.date.split('/');
                const dateStr = `${yearVal}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                
                // Only override if it's not already a holiday, leave, or WFH
                if (!eventMap[dateStr] || !['holiday', 'leave', 'wfh'].includes(eventMap[dateStr].type)) {
                    if (isOutNow(log)) {
                        // Out mid-shift: they did come in, and whether the day
                        // ends up a Half Day is not known until the shift is over.
                        eventMap[dateStr] = { type: 'present', label: attendanceStatusLabel(log) };
                    } else if (log.status === 'Half Day') {
                        eventMap[dateStr] = { type: 'half-day', label: 'Half Day' };
                    } else if (log.status === 'Absent') {
                        eventMap[dateStr] = { type: 'absent', label: 'Absent' };
                    } else if (log.status === 'Present' || log.status === 'Late') {
                        // 👇 NEW: Log Present Status
                        eventMap[dateStr] = { type: 'present', label: 'Present' };
                    }
                }
            });
        }
        setEvents(eventMap);
    };

    const getDateStatus = (dateStr, dayOfWeek) => {
        if (events[dateStr]) return `status-${events[dateStr].type}`;
        if (dayOfWeek === 0) return 'status-sunday';
        return '';
    };

    // --- NAVIGATION HANDLERS ---
    const handlePrevMonth = () => {
        setSlideDirection('left'); 
        setCurrentMonthIndex(prev => (prev === 0 ? 11 : prev - 1));
    };

    const handleNextMonth = () => {
        setSlideDirection('right'); 
        setCurrentMonthIndex(prev => (prev === 11 ? 0 : prev + 1));
    };

    // --- RENDER HELPERS ---
    const renderDaysGrid = (monthIndex) => {
        const firstDay = new Date(year, monthIndex, 1).getDay();
        const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
        let days = [];
        
        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dateObj = new Date(year, monthIndex, day);
            const statusClass = getDateStatus(dateStr, dateObj.getDay());
            const eventLabel = events[dateStr] ? events[dateStr].label : null;

            const todays = meetingsEnabled ? (meetingsByDay[dateStr] || []) : [];
            if (todays.length > 0) {
                const title = [eventLabel, `${todays.length} ${todays.length === 1 ? 'meeting or event' : 'meetings and events'}`]
                    .filter(Boolean).join(' · ');
                days.push(
                    <div
                        key={day}
                        className={`calendar-day ${statusClass} has-meetings${selectedDay === dateStr ? ' selected' : ''}`}
                        title={title}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedDay(selectedDay === dateStr ? null : dateStr)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setSelectedDay(selectedDay === dateStr ? null : dateStr);
                            }
                        }}
                    >
                        {day}
                        <span className="cal-meeting-dot" aria-hidden="true" />
                    </div>
                );
                continue;
            }

            days.push(
                <div key={day} className={`calendar-day ${statusClass}`} title={eventLabel || ''}>
                    {day}
                </div>
            );
        }
        return days;
    };

    const MonthCard = ({ monthName, index, isSlider }) => (
        <div className={`month-card ${isSlider ? 'slider-mode' : ''}`}>
            <div className="month-card-header">
                {isSlider && (
                    <button className="month-nav-btn" onClick={handlePrevMonth}>
                        <FontAwesomeIcon icon={faChevronLeft} size="lg" />
                    </button>
                )}
                
                <h3 className={`month-title ${isSlider ? 'slider-title' : 'grid-title'}`}>
                    {monthName} {isSlider && year}
                </h3>
                
                {isSlider && (
                    <button className="month-nav-btn" onClick={handleNextMonth}>
                        <FontAwesomeIcon icon={faChevronRight} size="lg" />
                    </button>
                )}
            </div>
            
            <div className="week-header">
                <span>S</span><span>M</span><span>T</span><span>W</span><span>T</span><span>F</span><span>S</span>
            </div>
            <div className="days-grid">
                {renderDaysGrid(index)}
            </div>
        </div>
    );

    if (loading) return <div className="main-content">Loading Calendar...</div>;

    return (
        <div className="calendar-page-container fade-in">
            <div className="calendar-top-section">
                <div className="calendar-header-row">
                    <h1 className="page-title header-no-margin">Calendar {year}</h1>
                    
                    {/* MINIMALIST VIEW TOGGLE */}
                    <div className="view-toggle-container">
                        <button 
                            onClick={() => setViewMode('slider')}
                            className={`view-toggle-btn ${viewMode === 'slider' ? 'active' : ''}`}
                        >
                            <FontAwesomeIcon icon={faCalendarDay} className="toggle-icon" /> Month
                        </button>
                        <button 
                            onClick={() => setViewMode('full')}
                            className={`view-toggle-btn ${viewMode === 'full' ? 'active' : ''}`}
                        >
                            <FontAwesomeIcon icon={faTh} className="toggle-icon" /> Year
                        </button>
                    </div>
                </div>

                {/* 👇 NEW: Added WFH and Present to Legend */}
                <div className="calendar-legend" style={{ flexWrap: 'wrap' }}>
                    <div className="legend-item"><span className="dot present"></span> Present</div>
                    <div className="legend-item"><span className="dot wfh"></span> WFH</div>
                    <div className="legend-item"><span className="dot leave"></span> On Leave</div>
                    <div className="legend-item"><span className="dot half-day"></span> Half Day</div>
                    <div className="legend-item"><span className="dot holiday"></span> Holiday</div>
                    <div className="legend-item"><span className="dot absent"></span> Absent</div>
                    <div className="legend-item"><span className="dot sunday"></span> Sunday</div>
                    {meetingsEnabled && (
                        <div className="legend-item"><span className="dot meeting"></span> Meeting / Event</div>
                    )}
                </div>
            </div>

            <div className="calendar-content">
                {viewMode === 'slider' ? (
                    <div className="slider-view-wrapper">
                        <div 
                            key={currentMonthIndex} 
                            className={`slider-anim-container ${slideDirection === 'right' ? 'slide-in-right' : 'slide-in-left'}`}
                        >
                            <MonthCard 
                                monthName={months[currentMonthIndex]} 
                                index={currentMonthIndex} 
                                isSlider={true} 
                            />
                        </div>
                    </div>
                ) : (
                    <div className="year-grid fade-in">
                        {months.map((month, index) => (
                            <MonthCard 
                                key={month} 
                                monthName={month} 
                                index={index} 
                                isSlider={false} 
                            />
                        ))}
                    </div>
                )}
            </div>

            {meetingsEnabled && selectedDay && dayMeetings.length > 0 && (
                <section className="cal-day-panel">
                    <h4>
                        {dayText(`${selectedDay}T12:00:00`, { weekday: 'long', day: 'numeric', month: 'long' })}
                    </h4>
                    {dayMeetings.map(m => {
                        const cancelled = m.status === 'cancelled';
                        return (
                            <button
                                key={m._id}
                                type="button"
                                className={`mt-card ${cancelled ? 'is-cancelled' : ''} ${!cancelled && m.myRsvp === 'pending' ? 'needs-reply' : ''}`}
                                onClick={() => setOpenMeetingId(m._id)}
                            >
                                <span className="mt-card-time">{rangeText(m)}</span>
                                <span className="mt-card-main">
                                    <span className="mt-card-title">{m.title}</span>
                                    <span className="mt-card-meta">
                                        <span>{m.kind === 'meeting' ? 'Meeting' : 'Event'}</span>
                                        {m.location && <span>{m.location}</span>}
                                    </span>
                                </span>
                                {m.myRsvp && m.myRole !== 'organizer' && !cancelled && (
                                    <span className={`mt-rsvp-tag ${m.myRsvp}`}>{RSVP_LABELS[m.myRsvp]}</span>
                                )}
                            </button>
                        );
                    })}
                </section>
            )}

            {meetingsEnabled && openMeeting && !editingMeeting && (
                <MeetingDrawer
                    meeting={openMeeting}
                    onClose={() => setOpenMeetingId(null)}
                    onChanged={(fresh) => setMeetings(list => list.map(m => (m._id === fresh._id ? fresh : m)))}
                    onEdit={(m) => setEditingMeeting(m)}
                />
            )}

            {meetingsEnabled && editingMeeting && (
                <MeetingFormModal
                    meeting={editingMeeting}
                    currentUser={currentUser}
                    onClose={() => setEditingMeeting(null)}
                    onSaved={(fresh) => {
                        setEditingMeeting(null);
                        setOpenMeetingId(fresh._id);
                        loadMeetings();
                    }}
                />
            )}
        </div>
    );
};

export default CalendarPage;